import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Search,
  ClipboardList,
  User,
  LogOut,
  LayoutDashboard,
  Menu,
  X,
  TrendingUp,
  ChevronDown,
  Wallet,
} from "lucide-react";
import { Logo } from "./ui";
import { useAuth } from "../context/AuthContext";
import NotificationBell from "./NotificationBell";
import { ProfileAvatar } from "./ProfilePhoto";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  const doLogout = async () => {
    setProfileOpen(false);
    await logout();
    navigate("/login");
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isAdmin = user?.role === "ADMIN";
  const isWorker = user?.role === "WORKER";
  const isCustomer = user?.role === "CUSTOMER";

  const profilePath = isAdmin ? "/admin" : isWorker ? "/worker/profile" : "/customer/profile";
  const displayName = user?.profile?.display_name || (isAdmin ? "Admin" : isWorker ? "Pekerja" : "Pelanggan");
  const initials = (displayName || "U")[0].toUpperCase();

  const navItems = isAdmin
    ? [
        { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
        { to: "/admin?tab=audit", label: "Audit", icon: ClipboardList },
        { to: "/admin?tab=config", label: "Konfigurasi", icon: User },
      ]
    : isWorker
    ? [
        { to: "/worker", label: "Beranda", icon: Home },
        { to: "/worker/bookings", label: "Pesanan", icon: ClipboardList },
        { to: "/worker/services", label: "Jasa Saya", icon: TrendingUp },
        { to: "/worker/finance", label: "Keuangan", icon: Wallet },
        { to: "/worker/profile", label: "Akun", icon: User },
      ]
    : [
        { to: "/customer", label: "Beranda", icon: Home },
        { to: "/customer/services", label: "Cari Jasa", icon: Search },
        { to: "/customer/bookings", label: "Pesanan", icon: ClipboardList },
        { to: "/customer/profile", label: "Akun", icon: User },
      ];

  const getBasePath = () => {
    if (isAdmin) return "/admin";
    if (isWorker) return "/worker";
    return "/customer";
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f6f8fc]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-brand-50">
        <div className="container-px flex items-center justify-between h-16 gap-4">
          <Link to={getBasePath()} className="flex-shrink-0">
            <Logo compact={isWorker || isAdmin} />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1 flex-1 justify-center" data-testid="desktop-nav">
            {navItems.map((it) => {
              const currentPath = location.pathname;
              const itPath = it.to.split("?")[0];
              const active = currentPath === itPath;
              return (
                <Link
                  key={it.label}
                  to={it.to}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition ${
                    active ? "bg-brand-50 text-brand-700" : "text-brand-500 hover:bg-brand-50 hover:text-brand-700"
                  }`}
                  data-testid={`desktop-nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <it.icon className="h-4 w-4" />
                  {it.label}
                </Link>
              );
            })}
          </nav>

          {/* Desktop profile dropdown */}
          <div className="hidden md:flex items-center gap-1 flex-shrink-0">
            <NotificationBell />
            <div className="relative" ref={profileRef}>
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-brand-50 transition"
              data-testid="profile-dropdown-trigger"
            >
              <div className="h-8 w-8 rounded-full overflow-hidden grid place-items-center">
                <ProfileAvatar
                  userId={user?.id}
                  photoUrl={user?.profile?.profile_photo_url}
                  name={displayName}
                  className="h-8 w-8 rounded-full"
                  fallbackClassName="h-8 w-8 rounded-full bg-brand-100 grid place-items-center text-sm font-bold text-brand-700"
                />
              </div>
              <span className="text-sm font-semibold text-brand-700">{displayName}</span>
              <ChevronDown className={`h-4 w-4 text-brand-400 transition ${profileOpen ? "rotate-180" : ""}`} />
            </button>

            {profileOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-lg border border-brand-100 py-2 animate-fade-up">
                <div className="px-4 py-2 border-b border-brand-50">
                  <p className="text-sm font-bold text-brand-800">{displayName}</p>
                  <p className="text-xs text-brand-400">
                    {isAdmin ? `Admin • ${user.admin_role}` : isWorker ? "Pekerja" : "Pelanggan"}
                  </p>
                </div>
                <Link
                  to={profilePath}
                  onClick={() => setProfileOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-brand-700 hover:bg-brand-50 transition"
                  data-testid="profile-dropdown-profile"
                >
                  <User className="h-4 w-4" /> Profil Saya
                </Link>
                <button
                  onClick={doLogout}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition w-full text-left"
                  data-testid="profile-dropdown-logout"
                >
                  <LogOut className="h-4 w-4" /> Keluar
                </button>
              </div>
            )}
            </div>
          </div>

          {/* Mobile: bell + menu toggle */}
          <div className="flex md:hidden items-center gap-1 flex-shrink-0">
            <NotificationBell />
            <button className="p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-6 w-6 text-brand-700" /> : <Menu className="h-6 w-6 text-brand-700" />}
          </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden container-px pb-4 flex flex-col gap-2">
            <div className="flex items-center gap-3 px-3 py-2.5 pt-3">
              <ProfileAvatar
                userId={user?.id}
                photoUrl={user?.profile?.profile_photo_url}
                name={displayName}
                className="h-9 w-9 rounded-full"
                fallbackClassName="h-9 w-9 rounded-full bg-brand-100 grid place-items-center text-sm font-bold text-brand-700"
              />
              <div>
                <p className="font-semibold text-brand-800 text-sm">{displayName}</p>
                <p className="text-xs text-brand-400">
                  {isAdmin ? `Admin • ${user?.admin_role}` : isWorker ? "Pekerja" : "Pelanggan"}
                </p>
              </div>
            </div>
            {navItems.map((it) => (
              <Link
                key={it.label}
                to={it.to}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-brand-700 hover:bg-brand-50 transition"
              >
                <it.icon className="h-5 w-5" />
                <span className="font-semibold">{it.label}</span>
              </Link>
            ))}
            <Link
              to={profilePath}
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-brand-700 hover:bg-brand-50 transition"
            >
              <User className="h-5 w-5" />
              <span className="font-semibold">Profil</span>
            </Link>
            <button onClick={doLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 hover:bg-red-50 transition mt-2" data-testid="logout-button-mobile">
              <LogOut className="h-5 w-5" />
              <span className="font-semibold">Keluar</span>
            </button>
          </div>
        )}
      </header>

      <main className="flex-1 container-px py-6 pb-28 md:pb-10 w-full">{children}</main>

      {/* Bottom nav (mobile) */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-brand-100 shadow-[0_-4px_20px_-8px_rgba(15,61,145,0.15)]">
        <div
          className="grid"
          style={{ gridTemplateColumns: `repeat(${navItems.length}, minmax(0, 1fr))` }}
        >
          {navItems.map((it) => {
            const currentPath = location.pathname;
            const itPath = it.to.split("?")[0];
            const active = currentPath === itPath || (itPath === getBasePath() && currentPath === itPath);
            return (
              <Link
                key={it.label}
                to={it.to}
                className={`flex flex-col items-center gap-1 py-2.5 ${active ? "text-brand-600" : "text-brand-300"}`}
                data-testid={`bottomnav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <it.icon className="h-6 w-6" />
                <span className="text-xs font-semibold">{it.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
